'use client';

// Package Imports
import React, { useRef, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { signOut } from 'next-auth/react';
import Link from 'next/link';
import { navAccount } from '@/lib/data/site/navAccount';

// Context Imports
import { useOrder } from '@/lib/context/orderContext';

// Function Imports
import { recreateCartWithItems } from '@/lib/functions/recreateCartWithItems';

// Style Imports
import styles from './accountHeader.module.scss';

const normalizePath = (p: string) => {
  if (!p) return '/';
  let s = p.startsWith('/') ? p : `/${p}`;
  if (s.length > 1 && s.endsWith('/')) s = s.slice(0, -1);
  return s;
};

const matches = (path: string, href: string) => {
  const p = normalizePath(path);
  const t = normalizePath(href);
  if (t === '/') return p === '/';
  return p === t || p.startsWith(`${t}/`);
};

export default function AccountHeader() {
  const pathnameRaw = usePathname() || '/';
  const pathname = normalizePath(pathnameRaw);
  const { state: orderState, dispatch: orderDispatch } = useOrder();

  const activeHref: string | null =
    [...navAccount]
      .map((l) => normalizePath(l.href))
      .filter((t: string) => matches(pathname, t))
      .sort((a, b) => b.length - a.length)[0] || null;

  const isActive = (href: string) => normalizePath(href) === activeHref;

  const activeLinkRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (activeLinkRef.current) activeLinkRef.current.focus();
  }, [pathname]);

  const handleLogOut = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    const previousToken = orderState.token;

    if (previousToken) {
      try {
        const result = await recreateCartWithItems(orderState, orderDispatch);
        if (result.token === previousToken) {
          console.error('Cart token did not change before logout; cart may still have customer shipping data.');
        }
      } catch (err) {
        console.error('Cart recreation failed before logout:', err);
      }
    }

    if (pathname !== '/my-account') {
      await signOut({ callbackUrl: '/my-account/' });
    } else {
      await signOut();
    }
  };

  return (
    <section className={styles.accountHeader}>
      <div className={styles.navWrapper}>
        <ul>
          {navAccount.map((link) => {
            const active = isActive(link.href);
            return (
              <li key={link.title}>
                <Link
                  href={link.href}
                  className={active ? styles.isActive : ''}
                  ref={active ? activeLinkRef : null}
                  aria-current={active ? 'page' : undefined}
                >
                  {link.title}
                </Link>
              </li>
            );
          })}
          <li>
            <Link href="/" onClick={handleLogOut}>Log Out</Link>
          </li>
        </ul>
      </div>
    </section>
  );
}
